import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Home, ChevronRight, Calendar, ArrowLeft, ArrowRight } from "lucide-react";
import { RichTextContent } from "../components/RichTextContent";

interface NewsDetail {
  id: number;
  information_title: string;
  information_content: string;
  article_type: string;
  tag: string;
  is_top: number;
  release_time: string;
  create_time: string;
}

export default function CompanyCultureDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<NewsDetail | null>(null);
  const [allNews, setAllNews] = useState<NewsDetail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchNews(id);
      fetchAllNews();
    }
  }, [id]);

  const fetchNews = async (articleId: string) => {
    try {
      const resp = await fetch(`/api/public/information/${articleId}`);
      const data = await resp.json();
      if (data.ok) {
        setNews(data.data);
      }
    } catch (err) {
      console.error('获取文章详情失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllNews = async () => {
    try {
      const resp = await fetch('/api/public/information/company-culture');
      const data = await resp.json();
      if (data.ok) {
        setAllNews(data.data || []);
      }
    } catch (err) {
      console.error('获取文章列表失败:', err);
    }
  };

  // 格式化日期时间
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).replace(/\//g, '-');
  };

  // 获取上一篇和下一篇
  const getPrevNext = () => {
    if (!id || allNews.length === 0) return { prev: null, next: null };
    const currentId = parseInt(id);
    const currentIndex = allNews.findIndex(n => n.id === currentId);
    if (currentIndex === -1) return { prev: null, next: null };
    return {
      prev: currentIndex > 0 ? allNews[currentIndex - 1] : null,
      next: currentIndex < allNews.length - 1 ? allNews[currentIndex + 1] : null
    };
  };

  const { prev, next } = getPrevNext();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Header />
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>加载中...</p>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <Header />
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>文章不存在</p>
          <Link to="/company-culture" style={{ color: '#1e3a5f', marginTop: '16px', display: 'inline-block' }}>
            返回企业文化列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #3d7ab5 100%)',
        padding: '48px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }} />
        <div style={{ width: '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {news.tag && (
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#fbbf24',
              borderRadius: '20px',
              fontSize: '14px',
              color: '#1f2937',
              fontWeight: 600,
              marginBottom: '16px'
            }}>
              {news.tag}
            </span>
          )}
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: 700, 
            color: 'white',
            lineHeight: 1.4,
            maxWidth: '900px'
          }}>
            {news.information_title}
          </h1>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ width: '80%', margin: '0 auto', padding: '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}>
              <Home style={{ width: '16px', height: '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: '16px', height: '16px', color: '#d1d5db' }} />
            <Link to="/company-culture" style={{ color: '#6b7280', textDecoration: 'none' }}>企业新闻</Link>
            <ChevronRight style={{ width: '16px', height: '16px', color: '#d1d5db' }} />
            <span style={{ color: '#1e3a5f', fontWeight: 500 }}>文章详情</span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: '80%', margin: '0 auto', padding: '40px 0' }}>
        <div style={{ display: 'flex', gap: '40px' }}>
          {/* 文章内容 */}
          <div style={{ flex: 1 }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}>
              {/* 文章元信息 */}
              <div style={{
                padding: '24px 40px',
                borderBottom: '1px solid #f3f4f6',
                background: 'linear-gradient(to right, #f8fafc, #f0f9ff)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '24px', fontSize: '14px', color: '#6b7280' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Calendar style={{ width: '16px', height: '16px', color: '#1e3a5f' }} />
                    发布时间：{formatDateTime(news.release_time)}
                  </span>
                </div>
              </div>

              {/* 文章正文 */}
              <div style={{ padding: '40px' }}>
                <RichTextContent
                  html={news.information_content}
                  style={{ fontSize: "16px", lineHeight: 1.9, color: "#374151" }}
                />
              </div>

              {/* 上一篇/下一篇 */}
              <div style={{
                padding: '24px 40px',
                borderTop: '1px solid #f3f4f6',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '24px'
              }}>
                {prev ? (
                  <Link
                    to={`/company-culture/${prev.id}`}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '16px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <ArrowLeft style={{ width: '20px', height: '20px', color: '#6b7280', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>上一篇</div>
                      <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.5 }}>
                        {prev.information_title.length > 30 ? prev.information_title.slice(0, 30) + '...' : prev.information_title}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div style={{ flex: 1 }} />
                )}
                
                {next ? (
                  <Link
                    to={`/company-culture/${next.id}`}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '12px',
                      padding: '16px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      textAlign: 'right',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>下一篇</div>
                      <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.5 }}>
                        {next.information_title.length > 30 ? next.information_title.slice(0, 30) + '...' : next.information_title}
                      </div>
                    </div>
                    <ArrowRight style={{ width: '20px', height: '20px', color: '#6b7280', flexShrink: 0 }} />
                  </Link>
                ) : (
                  <div style={{ flex: 1 }} />
                )}
              </div>
            </div>
          </div>

          {/* 侧边栏 */}
          <div style={{ width: '280px', flexShrink: 0 }}>
            {/* 热门文章 */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>热门文章</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {allNews
                  .filter(n => n.id !== news.id)
                  .slice(0, 5)
                  .map((n, index) => (
                    <Link
                      key={n.id}
                      to={`/company-culture/${n.id}`}
                      style={{
                        display: 'flex',
                        gap: '12px',
                        textDecoration: 'none',
                        paddingBottom: '16px',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      <span style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        backgroundColor: index < 3 ? '#1e3a5f' : '#e5e7eb',
                        color: index < 3 ? 'white' : '#6b7280',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {index + 1}
                      </span>
                      <span style={{
                        fontSize: '14px',
                        color: '#4b5563',
                        lineHeight: 1.5
                      }}>
                        {n.information_title.length > 25 ? n.information_title.slice(0, 25) + '...' : n.information_title}
                      </span>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

